import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Typography, Collapse, Divider, useMediaQuery } from '@mui/material'
import { Close, ChevronLeft } from '@mui/icons-material'
import { navList, navAdminList } from '_nav'
import { useAdmin } from 'services/hooks/useAdmin'
import CompanyPopper from '../header/components/CompanyPopper'
import AdxPopper from 'components/reusable/AdxPopper'
import AdxToolTip from 'components/custom/AdxToolTip'
import useFilteredMenu from './hooks'

const SideMenu = ({ handleDrawer, menuShown, setMenuShown }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [pathRoot, pathSection, pathSub] = pathname.split('/').filter(Boolean)

  const [isAdmin, permissions] = useAdmin()
  const [selectedTab, setSelectedTab] = useState(pathSection)
  const [selectedSubTab, setSelectedSubTab] = useState(`/${pathSub}`)
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))

  const { filteredMenus } = useFilteredMenu()

  const determineSubTabInitialState = () => {
    const initialState = {}
    const checkList = (list) => {
      list.forEach((item) => {
        if (item.items) {
          const path = `/${pathRoot}/${pathSection}/${pathSub}`
          if (
            item.items.some(
              (subItem) =>
                `${item.link}${subItem.link}` === path ||
                (subItem.alternateRoute && subItem.alternateRoute === pathSub)
            )
          ) {
            initialState[item.link] = true
          }
        }
      })
    }
    checkList([...navList, ...navAdminList])
    return initialState
  }

  const [subTabVisible, setSubTabVisible] = useState(determineSubTabInitialState)

  const toggleSubTab = (item) => {
    setSubTabVisible((prev) => ({ ...prev, [item.link]: !prev[item.link] }))
  }

  const handleClickTab = (item) => {
    if (item.items) {
      toggleSubTab(item)
    } else {
      setSelectedTab(item.link)
      navigate(item.link)
    }
  }

  const handleClickSubTab = (item, el) => {
    const path = `${item.link}${el.link}`
    setSelectedSubTab(el.link)
    navigate(path)
    toggleSubTab(item)
  }

  const styles = {
    menuItem: {
      cursor: 'pointer',
      height: 45,
      display: 'flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
      width: menuShown ? 1 : 'fit-content',
    },
    selectedMenuItem: {
      bgcolor: 'primary.light',
    },
    iconItem: {
      height: 35,
      width: 35,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    selectedIconItem: {
      bgcolor: 'primary.main',
      borderRadius: '100%',
    },
    hoverEffect: {
      ':hover': { bgcolor: 'gray-menu-hover.main' },
    },
  }

  // Subcomponents
  const MenuItem = ({ item, isSelected, onClick, children }) => (
    <AdxToolTip title={!menuShown ? item.title : ''} placement="right" disableInteractive arrow>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={0.75}
        borderRadius={'500px'}
        {...(isSelected && styles.selectedMenuItem)}
        color={isSelected ? 'primary.contrastText' : '#65676b'}
        sx={{
          ...styles.menuItem,
          ':hover': isSelected && !item?.items ? {} : styles.hoverEffect[':hover'],
        }}
        onClick={onClick}
      >
        {children}
      </Box>
    </AdxToolTip>
  )

  const IconItem = ({ item, isSelected, children }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderRadius={'100%'}
      {...(isSelected && styles.selectedIconItem)}
      color="#65676b"
      sx={{
        ...styles.iconItem,
        ':hover': isSelected && !item?.items ? {} : styles.hoverEffect[':hover'],
      }}
    >
      {children}
    </Box>
  )

  const SubMenuItem = ({ el, item, isSelected, onClick }) => (
    <Box sx={{ display: 'flex' }}>
      {isSelected && (
        <Box
          sx={{
            bgcolor: 'primary.main',
            height: 45,
            width: '3px',
            ml: -1.3,
            mr: 1,
            borderRadius: '500px',
          }}
        />
      )}
      <Box
        px={2}
        mb={0.625}
        borderRadius={'500px'}
        onClick={onClick}
        bgcolor={isSelected ? 'primary.light' : ''}
        color={isSelected ? 'primary.contrastText' : '#65676b'}
        sx={{
          ...styles.menuItem,
          ':hover': isSelected ? {} : styles.hoverEffect,
        }}
      >
        <Typography variant="body1">{el.title}</Typography>
      </Box>
    </Box>
  )

  return (
    <>
      <Box
        position={'fixed'}
        height="100vh"
        width={isLarge ? (menuShown ? '320px' : '100px') : menuShown ? '320px' : '0px'}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'space-between'}
        sx={{
          px: isLarge ? 3 : menuShown ? 1 : 0,
          py: isLarge ? 3 : menuShown ? 1 : 0,
          borderRight: isLarge ? '1px solid #e5e7eb' : menuShown ? '1px solid #e5e7eb' : 'none',
          bgcolor: isLarge ? 'white.main' : menuShown ? 'white.main' : 'transparent',
          zIndex: 51,
          overflowX: 'clip',
          // transition: 'width 0.3s ease-in-out, transform 0.3s ease-in-out',
          visibility: !isLarge && !menuShown ? 'hidden' : 'visible',
          transform: !isLarge && !menuShown ? 'translateX(-100%)' : 'translateX(0)',
        }}
      >
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Box
            sx={{
              px: 2,
              position: 'sticky',
              top: 0,
              height: '52px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              as="img"
              src={menuShown ? '/adnexio-logo-text.svg' : '/adnexio-logo-icon.svg'}
              onClick={() => navigate('/app/homepage')}
              height={20}
              sx={{ cursor: 'pointer' }}
            />
            <Close
              sx={{ cursor: 'pointer', display: { xs: 'block', lg: 'none' } }}
              onClick={() => setMenuShown(false)}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            minWidth={250}
            height={isLarge ? 'calc(100vh - 190px)' : 'calc(100vh - 170px)'}
            overflow={'auto'}
            className="scrollbar"
            gap={1}
          >
            {[
              { title: 'STAFF', items: navList || [] },
              ...(isAdmin
                ? [
                    {
                      title: 'ADMIN',
                      items: filteredMenus || [],
                    },
                  ]
                : []),
            ].map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                gap={1}
                justifyContent={'center'}
                alignItems={menuShown ? 'start' : 'start'}
              >
                {sectionIndex > 0 && (
                  <Box sx={{ width: menuShown ? 1 : '50px' }}>
                    <Divider />
                  </Box>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="bold"
                  pl={menuShown ? 1.5 : 0.75}
                  pt={sectionIndex > 0 && 1}
                  width={'fit-content'}
                >
                  {section?.title || ''}
                </Typography>

                {(section?.items || []).map((item, index) => (
                  <Box key={index} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {menuShown ? (
                      <>
                        <MenuItem
                          item={item}
                          isSelected={item?.baseRoute === (item?.items?.length ? '' : pathSection)}
                          onClick={() => {
                            item && handleClickTab(item)
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <IconItem
                              item={item}
                              isSelected={
                                item?.baseRoute === (item?.items?.length ? '' : pathSection)
                              }
                            >
                              {item?.icon}
                            </IconItem>
                            <Typography variant="body1" sx={{ textWrap: 'nowrap' }}>
                              {item?.title || 'Untitled'}
                            </Typography>
                          </Box>
                          {Array.isArray(item?.items) && item.items.length > 0 && (
                            <ChevronLeft
                              sx={{
                                rotate: subTabVisible[item?.link] ? '90deg' : '270deg',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          )}
                        </MenuItem>
                        {Array.isArray(item?.items) && item.items.length > 0 && (
                          <Collapse in={subTabVisible[item?.link]}>
                            <Box display="flex" justifyContent="flex-start" width="100%" pt={0.75}>
                              <Box sx={{ pl: isLarge ? 3 : 2.5, pr: 1, mb: 0.75 }}>
                                <Divider orientation="vertical" />
                              </Box>
                              <Box display="flex" flexDirection="column" width="100%">
                                {item.items.map((el, idx) => {
                                  return (
                                    <SubMenuItem
                                      key={idx}
                                      el={el}
                                      item={item}
                                      isSelected={
                                        el?.link?.substring(1) === pathSub ||
                                        (el?.alternateRoute && el?.alternateRoute === pathSub)
                                      }
                                      onClick={() => {
                                        el && item && handleClickSubTab(item, el)
                                      }}
                                    />
                                  )
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        )}
                      </>
                    ) : (
                      <AdxPopper
                        placement="right"
                        sx={{ marginLeft: 1 }}
                        customButton={
                          <MenuItem
                            item={item}
                            isSelected={item?.baseRoute === pathSection}
                            onClick={() => !item?.items && item && handleClickTab(item)}
                          >
                            <IconItem item={item} isSelected={item?.baseRoute === pathSection}>
                              {item?.icon}
                            </IconItem>
                          </MenuItem>
                        }
                      >
                        {(item?.items || []).map((el, idx) => (
                          <Box
                            key={idx}
                            onClick={() => el && item && handleClickSubTab(item, el)}
                            sx={{ display: 'flex', gap: 2 }}
                          >
                            <Typography variant="body1">{el?.title || 'Untitled'}</Typography>
                          </Box>
                        ))}
                      </AdxPopper>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <CompanyPopper menuShown={menuShown} />
        </Box>
      </Box>
      {menuShown === true && !isLarge && (
        <Box
          position={'fixed'}
          top={0}
          width={'100%'}
          height={'100vh'}
          bgcolor={'rgba(0, 0, 0, 0.5)'}
          visibility={'10%'}
          zIndex={50}
          onClick={() => setMenuShown(false)}
          sx={{ disableInteractive: true }}
        />
      )}
    </>
  )
}

export default SideMenu
